import { useCallback, useEffect, useRef, useState } from "react";

type CallPhase = "idle" | "connecting" | "live" | "ended";

interface VideoCallProps {
  selfName: string;
  selfAvatar?: string | null;
  peerName: string;
  peerAvatar?: string | null;
  peerRole: "rider" | "host";
}

function initial(name: string): string {
  return (name.trim().charAt(0) || "G").toUpperCase();
}

function clock(total: number): string {
  const m = Math.floor(total / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(total % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}

const BAR_HEIGHTS = [10, 18, 26, 14, 22, 12, 20, 16];

function AudioBars({ active, tone = "accent" }: { active: boolean; tone?: "accent" | "primary" }) {
  const color = tone === "accent" ? "bg-accent-400" : "bg-primary-400";
  return (
    <div className="flex h-6 items-end gap-[3px]">
      {BAR_HEIGHTS.map((height, index) => (
        <span
          key={index}
          className={`w-[3px] rounded-full ${color} ${active ? "gari-live-bar" : ""}`}
          style={{ height, animationDelay: `${index * 0.1}s` }}
        />
      ))}
    </div>
  );
}

function MiniTile({ name, avatar, label }: { name: string; avatar?: string | null; label: string }) {
  return (
    <div className="relative h-24 w-full overflow-hidden rounded-xl border border-background-200/70 bg-background-200/40">
      {avatar ? (
        <img src={avatar} alt={name} title={name} className="h-full w-full object-cover object-top" />
      ) : (
        <span className="flex h-full w-full items-center justify-center font-heading text-xl font-semibold text-foreground-600">
          {initial(name)}
        </span>
      )}
      <span className="absolute bottom-1.5 left-1.5 rounded-full bg-background-50/85 px-2 py-0.5 text-[10px] font-medium text-foreground-900 backdrop-blur">
        {label}
      </span>
    </div>
  );
}

export default function VideoCall({ selfName, selfAvatar, peerName, peerAvatar, peerRole }: VideoCallProps) {
  const [phase, setPhase] = useState<CallPhase>("idle");
  const [seconds, setSeconds] = useState(0);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [camReady, setCamReady] = useState(false);
  const [camBlocked, setCamBlocked] = useState(false);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
  }, []);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamBlocked(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.muted = true;
      }
      setCamReady(true);
      setCamBlocked(false);
    } catch {
      setCamReady(false);
      setCamBlocked(true);
    }
  }, []);

  // Connecting handshake, then go live (simulated peer stream).
  useEffect(() => {
    if (phase !== "connecting") return undefined;
    startCamera();
    const timer = window.setTimeout(() => setPhase("live"), 2000);
    return () => window.clearTimeout(timer);
  }, [phase, startCamera]);

  // Call duration ticker.
  useEffect(() => {
    if (phase !== "live") return undefined;
    const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCall = () => {
    setSeconds(0);
    setMicOn(true);
    setCamOn(true);
    setCamBlocked(false);
    setPhase("connecting");
  };

  const endCall = () => {
    stopCamera();
    setCamReady(false);
    setPhase("ended");
  };

  const closeCall = () => {
    stopCamera();
    setCamReady(false);
    setCamBlocked(false);
    setPhase("idle");
    setSeconds(0);
  };

  const toggleMic = () => {
    const next = !micOn;
    setMicOn(next);
    streamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = next;
    });
  };

  const toggleCam = () => {
    const next = !camOn;
    setCamOn(next);
    streamRef.current?.getVideoTracks().forEach((track) => {
      track.enabled = next;
    });
    if (next && !streamRef.current) startCamera();
  };

  const showSelfVideo = camReady && camOn;

  return (
    <>
      <div className="rounded-2xl border border-background-200/70 bg-background-100 p-5">
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
            <i className="ri-vidicon-line text-primary-500"></i> Live video meetup
          </p>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-200/60 px-2.5 py-1 text-[11px] text-foreground-600">
            <i className="ri-flashlight-line"></i> Simulated
          </span>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-foreground-600">
          Hop on a quick in-app video call with {peerName} to line up the handover and ask anything before you meet.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <MiniTile name={selfName} avatar={selfAvatar} label="You" />
          <MiniTile name={peerName} avatar={peerAvatar} label={peerRole === "host" ? "Host" : "Rider"} />
        </div>

        <button
          type="button"
          onClick={startCall}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-vidicon-fill"></i> Start live call
        </button>
      </div>

      {phase !== "idle" && (
        <div className="fixed inset-0 z-[1200] flex flex-col bg-background-50/97 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3 border-b border-background-200/70 px-4 py-3.5 md:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-background-200/60">
                {peerAvatar ? (
                  <img src={peerAvatar} alt={peerName} title={peerName} className="h-full w-full object-cover object-top" />
                ) : (
                  <span className="font-heading text-sm font-semibold text-foreground-600">{initial(peerName)}</span>
                )}
              </span>
              <div className="min-w-0">
                <p className="truncate font-heading text-sm font-semibold text-foreground-950">{peerName}</p>
                <p className="flex items-center gap-1.5 text-xs text-foreground-600">
                  {phase === "live" ? (
                    <>
                      <span className="flex h-1.5 w-1.5 rounded-full bg-accent-400 animate-rec-pulse"></span>
                      Live · {clock(seconds)}
                    </>
                  ) : phase === "connecting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> Connecting…
                    </>
                  ) : (
                    <>
                      <i className="ri-phone-fill"></i> Call ended · {clock(seconds)}
                    </>
                  )}
                </p>
              </div>
            </div>
            <span className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-background-200/60 px-3 py-1 text-[11px] text-foreground-600 sm:inline-flex">
              <i className="ri-flashlight-line"></i> Simulated stream
            </span>
          </div>

          <div className="relative flex-1 overflow-hidden bg-background-100">
            {peerAvatar ? (
              <img
                src={peerAvatar}
                alt={peerName}
                title={peerName}
                className={`absolute inset-0 h-full w-full object-cover object-top ${phase === "live" ? "animate-kenburns" : ""}`}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-background-100 to-background-200">
                <span className="font-heading text-7xl font-semibold text-foreground-500">{initial(peerName)}</span>
              </div>
            )}

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background-50/60 via-background-50/10 to-background-50/80" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-28 animate-scan bg-gradient-to-b from-primary-500/10 to-transparent" />

            <div className="pointer-events-none absolute left-4 top-4 z-10 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/85 px-3 py-1 text-[11px] font-medium text-foreground-900 backdrop-blur">
                <i className="ri-flashlight-line text-primary-500"></i> SIMULATED STREAM
              </span>
            </div>

            <div className="pointer-events-none absolute bottom-4 left-4 z-10 flex items-center gap-3 rounded-2xl bg-background-50/85 px-4 py-2.5 backdrop-blur">
              <AudioBars active={phase === "live" && micOn} />
              <div>
                <p className="text-xs font-medium text-foreground-900">{peerName}</p>
                <p className="text-[11px] text-foreground-600">
                  {phase === "live" ? (micOn ? "Speaking…" : "Muted") : "No audio yet"}
                </p>
              </div>
            </div>

            {(phase === "connecting" || phase === "ended") && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/85 text-2xl text-foreground-700 backdrop-blur">
                  <i className={phase === "connecting" ? "ri-loader-4-line animate-spin" : "ri-close-circle-line"}></i>
                </span>
                <p className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  {phase === "connecting" ? `Calling ${peerName}…` : "Call ended"}
                </p>
                <p className="mt-1 max-w-xs text-sm text-foreground-600">
                  {phase === "connecting"
                    ? "Ringing their device — this usually takes a few seconds."
                    : `You were on the call for ${clock(seconds)}.`}
                </p>
                {phase === "ended" && (
                  <div className="mt-5 flex flex-wrap justify-center gap-3">
                    <button
                      type="button"
                      onClick={startCall}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-refresh-line"></i> Call again
                    </button>
                    <button
                      type="button"
                      onClick={closeCall}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/50"
                    >
                      <i className="ri-close-line"></i> Close
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="absolute bottom-4 right-4 z-30 h-32 w-24 overflow-hidden rounded-xl border-2 border-background-50/70 bg-background-200/70 md:h-40 md:w-32">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className={`h-full w-full object-cover ${showSelfVideo ? "" : "hidden"}`}
              />
              {!showSelfVideo && (
                <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-center">
                  {camBlocked && !camOn ? (
                    <i className="ri-camera-off-line text-xl text-foreground-500"></i>
                  ) : selfAvatar ? (
                    <img src={selfAvatar} alt={selfName} title={selfName} className="h-full w-full object-cover object-top" />
                  ) : (
                    <span className="font-heading text-lg font-semibold text-foreground-500">{initial(selfName)}</span>
                  )}
                </div>
              )}
              <span className="absolute bottom-1 left-1 rounded-full bg-background-50/85 px-2 py-0.5 text-[10px] font-medium text-foreground-900">
                You
              </span>
            </div>
          </div>

          <div className="border-t border-background-200/70 px-4 py-4 md:px-6">
            {camBlocked && (
              <p className="mx-auto mb-3 flex max-w-xl items-center justify-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-2.5 text-xs text-secondary-300">
                <i className="ri-information-line"></i> Camera access isn't available here, so your self-view stays simulated.
              </p>
            )}
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={toggleMic}
                className={`inline-flex h-12 w-12 items-center justify-center rounded-full text-lg transition-colors ${
                  micOn ? "bg-background-200/70 text-foreground-900 hover:bg-background-300/70" : "bg-secondary-500 text-background-50"
                }`}
                aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
                title={micOn ? "Mute" : "Unmute"}
              >
                <i className={micOn ? "ri-mic-line" : "ri-mic-off-line"}></i>
              </button>
              <button
                type="button"
                onClick={endCall}
                disabled={phase === "ended"}
                className="inline-flex h-14 items-center gap-2 whitespace-nowrap rounded-full bg-secondary-500 px-7 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600 disabled:opacity-50"
              >
                <i className="ri-phone-fill rotate-[135deg]"></i> End call
              </button>
              <button
                type="button"
                onClick={toggleCam}
                className={`inline-flex h-12 w-12 items-center justify-center rounded-full text-lg transition-colors ${
                  camOn ? "bg-background-200/70 text-foreground-900 hover:bg-background-300/70" : "bg-secondary-500 text-background-50"
                }`}
                aria-label={camOn ? "Turn camera off" : "Turn camera on"}
                title={camOn ? "Camera off" : "Camera on"}
              >
                <i className={camOn ? "ri-vidicon-line" : "ri-camera-off-line"}></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}